import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Plus, Search, User as UserIcon, Phone, Mail, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export function UsersScreen() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<'all' | 'active' | 'expiring' | 'expired'>('all');
    const [page, setPage] = useState(1);

    // We can debounce search in a real app, for now direct.
    const { data: usersData, isLoading } = trpc.listUsers.useQuery({
        page,
        limit: 50,
        search: search || undefined,
        status
    });

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1); // Reset page on search
    };

    return (
        <div className="space-y-4 pb-20">
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
                    {['all', 'active', 'expiring', 'expired'].map((s) => (
                        <Button
                            key={s}
                            variant={status === s ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => { setStatus(s as any); setPage(1); }}
                            className="capitalize rounded-full"
                        >
                            {s}
                        </Button>
                    ))}
                </div>

                <div className="flex items-center justify-between gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="search"
                            placeholder="Search users..."
                            className="pl-8 bg-background"
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>
                    <Button size="icon" onClick={() => navigate('/users/new')}>
                        <Plus className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {isLoading && !usersData ? (
                <div className="text-center p-8 text-muted-foreground">Loading users...</div>
            ) : (
                <div className="grid gap-3">
                    {usersData?.users.map((user: any) => {
                        const today = new Date();
                        const endDate = user.last_end_date ? new Date(user.last_end_date) : null;
                        const daysLeft = endDate ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : -999;

                        let statusColor = "bg-gray-100 text-gray-500";
                        let statusText = "No Plan";

                        if (endDate) {
                            if (daysLeft < 0) {
                                statusColor = "bg-red-100 text-red-600";
                                statusText = "Expired";
                            } else if (daysLeft <= 7) {
                                statusColor = "bg-amber-100 text-amber-700";
                                statusText = `Expiring in ${daysLeft} days`;
                            } else {
                                statusColor = "bg-green-100 text-green-700";
                                statusText = "Active";
                            }
                        }

                        return (
                            <Card
                                key={user.id}
                                className="overflow-hidden border-0 shadow-sm cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={() => navigate(`/users/${user.id}`)}
                            >
                                <CardContent className="p-4 flex items-center gap-4">
                                    <Avatar className="h-12 w-12 border">
                                        <AvatarImage src={user.metadata ? JSON.parse(user.metadata).photo : undefined} />
                                        <AvatarFallback><UserIcon className="h-6 w-6 text-muted-foreground" /></AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <div className="font-semibold truncate pr-2">{user.name}</div>
                                            <div className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${statusColor} whitespace-nowrap`}>
                                                {statusText}
                                            </div>
                                        </div>
                                        <div className="text-sm text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                                            <Mail className="h-3 w-3" /> {user.email}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                            <Calendar className="h-3 w-3" /> Joined {format(new Date(user.created_at), 'MMM yyyy')}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}

                    {usersData?.users.length === 0 && (
                        <div className="text-center p-8 text-muted-foreground">No users found.</div>
                    )}
                </div>
            )}
        </div>
    );
}
